import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Icon, Label } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { DealerInfoModal } from "modules/DealersDashboard/components";
import { useGetDealersDashboardFilters } from "modules/DealersDashboard/components/DealersDashboardFilters/hooks";
import { DealerInvoicingCell, DealerLocationsTable } from "modules/DealersDashboard/components/DealersDashboardTable/components";
import "modules/DealersDashboard/components/DealersDashboardTable/DealersDashboardTable.scss";
import { useDealersQuery } from "modules/DealersDashboard/components/DealersDashboardTable/hooks";
import { DEALER_STATUSES } from "modules/DealersDashboard/queryKeys";
import { getHoursSinceImport } from "modules/DealersDashboard/utils";
import { PATHS } from "router/paths";

import type { ExpandedState } from "@tanstack/react-table";
import type { Dealer, DealerID, DealerLocation } from "models";

const columnHelper = createColumnHelper<Dealer>();

const BAD_IMPORT_HOURS = 1;

const countBadImports = (locations?: DealerLocation[]) =>
  locations?.reduce((count, location) => (getHoursSinceImport(location.last_import_at) >= BAD_IMPORT_HOURS ? count + 1 : count), 0) ?? 0;

type DealersDashboardTableProps = {
  onAddLocation: (dealerId: DealerID) => void;
};

export const DealersDashboardTable = ({ onAddLocation }: DealersDashboardTableProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const [dealerForInfo, setDealerForInfo] = useState<Dealer | null>(null);

  const { data: dealers, isLoading: isLoadingDealers, refetch: refetchDealers } = useDealersQuery();
  const {
    data: { term, status, areLocationsExpanded }
  } = useGetDealersDashboardFilters();

  useRegisterNavAction(refetchDealers);
  useEffect(() => setExpandedRows(areLocationsExpanded ? true : {}), [areLocationsExpanded]);

  const searchTerm = term.toLowerCase();
  const filteredDealers = dealers?.filter(dealer => {
    if (status === DEALER_STATUSES.Active && !dealer.active) return false;
    if (status === DEALER_STATUSES.Inactive && dealer.active) return false;
    if (!searchTerm) return true;

    return [dealer.name, dealer.sys_admin_name, dealer.sys_admin_email].some(field => field.toLowerCase().includes(searchTerm));
  });

  const handleToggleDealer = (dealer: Dealer) => {
    if (!dealer.locations?.length) return;

    setExpandedRows(previousExpandedRows => {
      const currentExpandedRows = previousExpandedRows === true ? Object.fromEntries(dealers?.map(({ id }) => [id, true]) ?? []) : previousExpandedRows;
      return { ...currentExpandedRows, [dealer.id]: !currentExpandedRows[dealer.id] };
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "dealer",
        size: 250,
        header: () => <span className="no-wrap-text">{t("v8_dealer", "Dealer")}</span>,
        cell: ({ row: { original: dealer, getIsExpanded } }) => (
          <div className="name-container">
            <div className={dealer.active ? "active" : "inactive"} />
            {!!dealer.locations?.length && (
              <div
                className="collapse-container"
                onClick={e => {
                  e.stopPropagation();
                  handleToggleDealer(dealer);
                }}
              >
                <Icon className={`caret ${getIsExpanded() ? "down" : "right"}`} />
              </div>
            )}
            <span className="no-wrap-text">{dealer.name}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "sys_admin_name",
        size: 130,
        header: () => <span className="no-wrap-text">{t("v8_claire_admin", "Claire Admin")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.sys_admin_name}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "invoicing",
        size: 170,
        header: () => <span className="no-wrap-text">{t("v8_invoicing", "Invoicing")}</span>,
        cell: ({ row: { original: dealer } }) => <DealerInvoicingCell dealer={dealer} />
      }),
      columnHelper.accessor(row => row, {
        id: "sys_admin_email",
        size: 170,
        header: () => <span className="no-wrap-text">{t("v8_email", "E-mail")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.sys_admin_email}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "sys_admin_phone",
        size: 150,
        header: () => <span className="no-wrap-text">{t("v8_phone", "Phone")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.sys_admin_phone}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "dms_server_info",
        size: 150,
        header: () => <span className="no-wrap-text">{t("v8_dms_info", "DMS Info")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.dms_server_info}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "bad_imports",
        size: 100,
        header: () => <span className="no-wrap-text">{t("v8_bad_imports", "Bad Imports")}</span>,
        cell: ({ row: { original } }) => {
          const count = countBadImports(original.locations);
          if (!count) return null;

          return (
            <Label circular color="red">
              {count}
            </Label>
          );
        }
      }),
      columnHelper.accessor(row => row, {
        id: "network_info",
        size: 180,
        header: () => <span className="no-wrap-text">{t("v8_network_info", "Network Info")}</span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.network_info}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 60,
        cell: ({ row: { original: dealer } }) => (
          <div className="actions-container">
            <div
              className="action-button-container"
              onClick={e => {
                e.stopPropagation();
                setDealerForInfo(dealer);
              }}
            >
              <Icon className="info action-button-icon-center" />
            </div>
            <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Locations}>
              <div
                className="action-add-button-container"
                title={t("v8_create_location", "Create location")}
                onClick={e => {
                  e.stopPropagation();
                  onAddLocation(dealer.id);
                }}
              >
                <Icon className="plus action-button-icon-center" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t, dealers, onAddLocation]
  );

  return (
    <>
      <div className="DealersDashboardTable">
        <ReactTable
          isFetching={isLoadingDealers}
          columns={columns}
          data={filteredDealers}
          expanded={expandedRows}
          onExpandedChange={setExpandedRows}
          getRowId={dealer => `${dealer.id}`}
          onRowClick={(dealer: Dealer) => history.push(`${PATHS.DEALERS_DASHBOARD}/${dealer.id}`)}
          renderExpandedComponent={row => <DealerLocationsTable dealer={row.original} />}
        />
      </div>
      <DealerInfoModal dealer={dealerForInfo} onClose={() => setDealerForInfo(null)} />
    </>
  );
};
